import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItemButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import { FancyButton } from '../comps/FancyButtons'
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useApp, useAppActions } from '../AppContext'
import { addToast } from '../services/Toasts'
import { FS_MICRO, FS_XS, ICON_MD, ICON_NAV } from '../common/theme'
import taapi from '../services/taapi'
import type { MentionItem, ToolEvent, ConvoMessage, Convo, TokenUsage, ChatIteration, ChatSection } from '../types'
import { type KbItem, fetchInit, treeNodeAt, fetchDirectory } from '../services/KnowledgeBaseService'
import { addPendingTask } from '../services/TaskManagerService'
import ChatPanel from './chat/ChatPanel'
import KbFolderPanel from './knowledgebase/KbFolderPanel'
import KbAddFilesModal from './modals/KbAddFilesModal'
import SearchingScope from '../comps/SearchingScope'

const DATE_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  prev7days: 'Prev 7 Days',
  prev30days: 'Prev 30 Days',
  older: 'Older',
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function QuickChatTab() {
  const { taskCount, health } = useApp()
  const { updateQuestion } = useAppActions()

  const [convoList, setConvoList] = useState<Convo[]>([])
  const [currentConvoIndex, setCurrentConvoIndex] = useState(-1)
  const [isProcessing, setIsProcessing] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [streamingText, setStreamingText] = useState<string | null>(null)
  const [streamingTools, setStreamingTools] = useState<ToolEvent[]>([])
  const [streamingThinking, setStreamingThinking] = useState<string | null>(null)
  const [streamingIterations, setStreamingIterations] = useState<ChatIteration[] | null>(null)
  const [streamingUsage, setStreamingUsage] = useState<TokenUsage | null>(null)

  const [folders, setFolders] = useState<KbItem[]>([])
  const [folderFiles, setFolderFiles] = useState<KbItem[]>([])
  const [foldersRefreshing, setFoldersRefreshing] = useState(false)
  const [folderPath, setFolderPath] = useState('/')
  const [folderDir, setFolderDir] = useState<KbItem | null>(null)
  const [folderTab, setFolderTab] = useState<'knowledge' | 'project'>('knowledge')
  const [addFilesOpen, setAddFilesOpen] = useState(false)
  const [addFilesInitial, setAddFilesInitial] = useState<File[]>([])
  const [selectedFolders, setSelectedFolders] = useState<Map<string, { name: string; type: 'folder' | 'file'; isProject?: boolean }>>(new Map())
  const [searchKnowledgeBase] = useState(false)

  const [sidebarWidth, setSidebarWidth] = useState(260)
  const isResizingRef = useRef(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(260)

  const [convoHeight, setConvoHeight] = useState(() => Math.round(window.innerHeight / 2))
  const isVResizingRef = useRef(false)
  const startYRef = useRef(0)
  const startHeightRef = useRef(240)

  const abortControllerRef = useRef<AbortController | null>(null)
  const mainContainerRef = useRef<HTMLDivElement>(null)
  const leftPanelRef = useRef<HTMLDivElement>(null)


  const currentConvo: Convo | undefined = convoList[currentConvoIndex]
  const hasConvos = convoList.length > 0
  const hasSelectedFolders = selectedFolders.size > 0

  useEffect(() => {
    loadConvos()
    loadFolders()
  }, [])

  // The dead `/api2/eventstream` SSE loop used to live here. The endpoint
  // never existed server-side and taskcount / health now arrive over the
  // taskstream WebSocket via AppContext, so nothing to do.

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return
      const delta = e.clientX - startXRef.current
      const maxW = mainContainerRef.current?.clientWidth ?? window.innerWidth
      setSidebarWidth(Math.max(0, Math.min(maxW, startWidthRef.current + delta)))
    }
    const onMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    isResizingRef.current = true
    startXRef.current = e.clientX
    startWidthRef.current = sidebarWidth
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isVResizingRef.current) return
      const delta = e.clientY - startYRef.current
      const maxH = (leftPanelRef.current?.clientHeight ?? window.innerHeight) - 7
      setConvoHeight(Math.max(0, Math.min(maxH, startHeightRef.current + delta)))
    }
    const onMouseUp = () => {
      if (isVResizingRef.current) {
        isVResizingRef.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  const handleVertResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    isVResizingRef.current = true
    startYRef.current = e.clientY
    startHeightRef.current = convoHeight
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
  }

  // ── Data loading ────────────────────────────────────────────────────────

  const loadConvos = async (): Promise<Convo[]> => {
    try {
      const result = await taapi.post('/api2/qainit', {})
      const list: Convo[] = result?.convolist ?? []
      setConvoList(list)
      return list
    } catch {
      addToast('error', 'Failed to load conversations.')
      return []
    }
  }

  const loadFolders = async (path: string = '/') => {
    setFoldersRefreshing(true)
    try {
      const tree = await fetchInit(path)
      if (tree) {
        const node = treeNodeAt(tree, path)
        setFolderPath(path)
        setFolderDir(node)
        setFolders((node.child ?? []).filter(c => c.isDirectory))
        if (node.dirsha || node.projectsha) {
          try {
            const items = await fetchDirectory(node.dirsha ?? null, node.projectsha ?? null)
            setFolderFiles(items)
          } catch {
            setFolderFiles([])
          }
        } else {
          setFolderFiles([])
        }
      }
    } catch {
      // Folders are non-critical
    } finally {
      setFoldersRefreshing(false)
    }
  }

  const openFolder = (folder: KbItem) => {
    const base = folderPath.endsWith('/') ? folderPath : folderPath + '/'
    loadFolders(base + folder.name)
  }

  const refreshFolderFiles = async () => {
    const dir = folderDir
    if (!dir || (!dir.dirsha && !dir.projectsha)) return
    try {
      const items = await fetchDirectory(dir.dirsha ?? null, dir.projectsha ?? null)
      setFolderFiles(items)
    } catch { /* ignore */ }
  }

  const handleFilesUploaded = (uploadedFiles: File[], taskshalist: string[]) => {
    const placeholders: KbItem[] = uploadedFiles.map(f => ({
      name: f.name,
      isDirectory: false,
      isIndexed: false,
      savedate: new Date().toISOString(),
    }))
    setFolderFiles(prev => [
      ...prev,
      ...placeholders.filter(p => !prev.some(e => e.name === p.name)),
    ])
    taskshalist.forEach(sha => {
      addPendingTask(sha, addToast, updateQuestion, () => refreshFolderFiles())
    })
  }

  const openAddFilesWithDropped = (fileList: FileList) => {
    setAddFilesInitial(Array.from(fileList))
    setAddFilesOpen(true)
  }

  const goHome = () => loadFolders('/')

  const goUp = () => {
    const parts = folderPath.replace(/\/$/, '').split('/')
    parts.pop()
    loadFolders(parts.join('/') || '/')
  }

  // ── Conversation selection ───────────────────────────────────────────────

  const handleSelectConvo = async (i: number) => {
    const sha = convoList[i]?.convosha
    setCurrentConvoIndex(i)
    try {
      const refreshed = await loadConvos()
      if (sha && refreshed.length > 0) {
        const newIndex = refreshed.findIndex(c => c.convosha === sha)
        setCurrentConvoIndex(newIndex >= 0 ? newIndex : i)
      }
    } catch {
      // Use cached data
    }
  }

  // ── Folder toggle ────────────────────────────────────────────────────────

  const handleFolderToggle = useCallback((item: KbItem) => {
    const sha = item.isDirectory
      ? (item.dirsha ?? item.projectsha ?? '')
      : (item.docsha ?? '')
    if (!sha) return
    const type: 'folder' | 'file' = item.isDirectory ? 'folder' : 'file'
    const isProject = type === 'folder' && !!item.projectsha
    setSelectedFolders(prev => {
      const next = new Map(prev)
      if (next.has(sha)) next.delete(sha)
      else next.set(sha, { name: item.name, type, isProject })
      return next
    })
  }, [])

  const removeSelected = useCallback((sha: string) => {
    setSelectedFolders(prev => {
      const next = new Map(prev)
      next.delete(sha)
      return next
    })
  }, [])

  const selectedShaSet = React.useMemo(() => new Set(selectedFolders.keys()), [selectedFolders])

  // Build the prefixed-sha list the qastream API decodes ("file:..", "folder:..", "project:..").
  // Mentions carry their own type; selections become "project" if isProject is set.
  const buildScopedShas = (mentions: { sha: string; type: 'file' | 'folder' }[]): string[] => {
    const out = new Set<string>()
    mentions.forEach(m => out.add(`${m.type}:${m.sha}`))
    selectedFolders.forEach((info, sha) => {
      const t = info.type === 'folder' && info.isProject ? 'project' : info.type
      out.add(`${t}:${sha}`)
    })
    return Array.from(out)
  }

  // ── Send / resend message (streaming) ────────────────────────────────────

  // Single shared streaming pipeline. Both new asks and resends end up here:
  // they just differ in what the outbound message list looks like and which
  // mention/scope refs to send. Sections within an iteration are keyed by
  // (iteration, id) - id "0" is the iteration's general text; other ids are
  // individual tool calls (so two calls of the same tool name no longer
  // collapse into one row).
  const runChatStream = async (
    outboundMessages: ConvoMessage[],
    mentionedShas: string[],
    activeIdx: number,
  ) => {
    setIsProcessing(true)
    setStreamingText('')
    setStreamingTools([])
    setStreamingThinking(null)
    setStreamingIterations(null)
    setStreamingUsage(null)

    let localUsage: TokenUsage | null = null
    const localIterations: ChatIteration[] = []

    const ensureSection = (iterIdx: number, sectionId: string): ChatSection => {
      while (localIterations.length <= iterIdx) localIterations.push({ sections: [] })
      const iter = localIterations[iterIdx]
      if (!iter.sections) iter.sections = []
      let section = iter.sections.find(s => s.id === sectionId)
      if (!section) {
        section = { id: sectionId }
        iter.sections.push(section)
      }
      return section
    }

    // ChatIteration now exposes only `sections` - IterationGroup derives
    // tools/text/thinking from them at render time, so we no longer maintain
    // those as extra fields on the iteration. We still surface a flat tools
    // list and a concatenated text for the legacy controlled-mode streaming
    // hooks (externalStreamingTools / externalStreamingText).
    const deriveTools = (iter: ChatIteration): ToolEvent[] =>
      (iter.sections ?? [])
        .filter(s => !!s.tool)
        .map(s => ({
          tool: s.tool!,
          status: s.status ?? '',
          result_schema: s.result_schema,
          result: s.result,
        }))

    // Concatenate all "0" section texts across iterations (used for legacy `q`).
    const computeFullText = () =>
      localIterations
        .map(it => (it.sections ?? []).find(s => s.id === '0')?.text ?? '')
        .join('')

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      // qastream now runs over a WebSocket. Each yielded value is one `data:`
      // payload (SSE-framed by the server), so the per-chunk handling below is
      // unchanged from the previous fetch-stream implementation.
      for await (const data of taapi.openStreamSocket('/api2/qastream', {
        messagelist: outboundMessages,
        systemprompt: null,
        mentionedShas,
        searchKnowledgeBase,
      }, controller.signal)) {
        if (data === '[DONE]') break
        let parsed: { tool?: string; status?: string; iteration?: number; text?: string; id?: string; usage?: TokenUsage; error?: string; result_schema?: string; result?: any }
        try { parsed = JSON.parse(data) } catch { continue }
        if (parsed.error) throw new Error(parsed.error)
        console.log('chunk', parsed);
        const iterIdx = parsed.iteration ?? 0
        const sectionId = parsed.id ?? '0'
        const section = ensureSection(iterIdx, sectionId)

        if (parsed.tool) section.tool = parsed.tool
        if (parsed.status) section.status = parsed.status
        if (parsed.result_schema) section.result_schema = parsed.result_schema
        if (parsed.result !== undefined) section.result = parsed.result
        if (parsed.text) section.text = (section.text ?? '') + parsed.text
        if (parsed.usage) {
          localUsage = { ...(localUsage ?? {}), ...parsed.usage }
          setStreamingUsage(localUsage)
        }

        // Push updates to the streaming display. Tools / text are
        // derived from the iteration's sections at every chunk so
        // controlled-mode consumers (externalStreamingTools/Text) see
        // the same flattened view IterationGroup will eventually render.
        setStreamingTools(deriveTools(localIterations[iterIdx]))
        setStreamingText(computeFullText())
        setStreamingIterations([...localIterations])
      }

      // Commit the assistant message. `q` keeps the concatenated text for any
      // legacy fallback rendering; `iterations` carries the structured shape.
      const finalText = computeFullText()
      const assistantMsg: ConvoMessage = {
        type: 'assistant',
        q: finalText,
        updatetime: new Date().toISOString(),
        usessources: hasSelectedFolders,
        iterations: localIterations.length > 0 ? localIterations : undefined,
        usage: localUsage ?? undefined,
      }
      setConvoList(prev => {
        const updated = [...prev]
        if (updated[activeIdx]) {
          updated[activeIdx] = {
            ...updated[activeIdx],
            messagelist: [...updated[activeIdx].messagelist, assistantMsg],
          }
        }
        return updated
      })
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        const partialText = computeFullText()
        if (partialText) {
          setConvoList(prev => {
            const updated = [...prev]
            if (updated[activeIdx]) {
              updated[activeIdx] = {
                ...updated[activeIdx],
                messagelist: [...updated[activeIdx].messagelist, {
                  type: 'assistant' as const,
                  q: partialText,
                  updatetime: new Date().toISOString(),
                  usessources: hasSelectedFolders,
                  stoppedManually: true,
                  iterations: localIterations.length > 0 ? localIterations : undefined,
                  usage: localUsage ?? undefined,
                }],
              }
            }
            return updated
          })
        }
      } else {
        console.error('[ChatTab] stream error:', err)
        addToast('error', 'Failed to get a response.')
      }
    } finally {
      abortControllerRef.current = null
      setStreamingText(null)
      setStreamingThinking(null)
      setStreamingIterations(null)
      setStreamingUsage(null)
      setIsProcessing(false)
    }
  }

  const handleAsk = async (q: string, currentMentions: MentionItem[]) => {
    if (!q || isProcessing) return

    const newUserMsg: ConvoMessage = { type: 'user', q, updatetime: new Date().toISOString() }
    const existingMessages = currentConvo?.messagelist ?? []
    const outboundMessageList = [...existingMessages, newUserMsg]

    let activeIdx: number
    if (currentConvoIndex >= 0) {
      activeIdx = currentConvoIndex
      setConvoList(prev => {
        const updated = [...prev]
        if (updated[activeIdx]) {
          updated[activeIdx] = {
            ...updated[activeIdx],
            messagelist: [...updated[activeIdx].messagelist, newUserMsg],
          }
        }
        return updated
      })
    } else {
      activeIdx = 0
      const tempConvo: Convo = {
        convosha: undefined,
        title: q.slice(0, 80),
        datecategory: 'today',
        updatetime: new Date().toISOString(),
        messagelist: [newUserMsg],
        sourcedocs: [],
      }
      setConvoList(prev => [tempConvo, ...prev])
      setCurrentConvoIndex(0)
    }

    await runChatStream(outboundMessageList, buildScopedShas(currentMentions), activeIdx)
  }

  // ── Delete conversation ─────────────────────────────────────────────────

  const handleDeleteConvo = async () => {
    if (currentConvoIndex < 0 || !currentConvo?.convosha) return
    try {
      const result = await taapi.get('/api2/qadeleteconvo', { convosha: currentConvo.convosha })
      if (result?.success) {
        addToast('success', 'Conversation deleted.')
        setCurrentConvoIndex(-1)
        loadConvos()
      } else {
        addToast('error', 'Failed to delete conversation.')
      }
    } catch {
      addToast('error', 'Failed to delete conversation.')
    }
  }

  const handleStop = () => {
    abortControllerRef.current?.abort()
  }

  const handleResend = async (msgIndex: number) => {
    if (isProcessing) return
    const convo = convoList[currentConvoIndex]
    if (!convo) return

    const truncated = convo.messagelist.slice(0, msgIndex + 1)
    const userMsg = truncated[truncated.length - 1]
    if (!userMsg || userMsg.type !== 'user') return

    // Pull mentions out of the stored message text. Newer messages serialize
    // chips as @name<type:sha>; older ones used @name<sha> - assume "file" then.
    const messageMentions: { sha: string; type: 'file' | 'folder' }[] = []
    const mentionRegex = /@[^<]+<([^>]+)>/g
    let match
    while ((match = mentionRegex.exec(userMsg.q)) !== null) {
      const inner = match[1]
      const m = /^(file|folder|project):(.+)$/.exec(inner)
      if (m) {
        const t = m[1] === 'project' ? 'folder' : (m[1] as 'file' | 'folder')
        messageMentions.push({ type: t, sha: m[2] })
      } else {
        messageMentions.push({ type: 'file', sha: inner })
      }
    }

    const activeIdx = currentConvoIndex

    setConvoList(prev => {
      const updated = [...prev]
      if (updated[activeIdx]) updated[activeIdx] = { ...updated[activeIdx], messagelist: truncated }
      return updated
    })

    await runChatStream(truncated, buildScopedShas(messageMentions), activeIdx)
  }

  // ── Render ──────────────────────────────────────────────────────────────

  const toolbarScopeContent = (
    <SearchingScope
      items={hasSelectedFolders
        ? Array.from(selectedFolders.entries()).map(([sha, info]) => ({ sha, ...info }))
        : undefined}
      onRemoveItem={removeSelected}
      onClear={() => setSelectedFolders(new Map())}
      currentPath={folderPath}
      currentDirName={folderDir?.name}
      currentFolderIsProject={!!folderDir?.projectsha}
      hint={!hasSelectedFolders ? '· Tick Folders to Scope' : undefined}
    />
  )

  const toolbarRightContent = (
    <>
      {taskCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <CircularProgress size={10} thickness={5} sx={{ color: 'warning.main' }} />
          <Typography variant="caption" sx={{ fontSize: FS_MICRO, color: 'text.secondary' }}>
            {taskCount} processing
          </Typography>
        </Box>
      )}
      <Tooltip title={health.success ? 'Service healthy' : health.msg || 'Service unavailable'}>
        <Box sx={{
          width: 7, height: 7, borderRadius: '50%',
          bgcolor: health.success ? 'success.main' : 'error.main',
          flexShrink: 0,
        }} />
      </Tooltip>
    </>
  )

  return (
    <Box ref={mainContainerRef} sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      <KbAddFilesModal
        open={addFilesOpen}
        dirsha={folderDir?.dirsha}
        initialFiles={addFilesInitial}
        existingNames={folderFiles.map(f => f.name)}
        onClose={() => setAddFilesOpen(false)}
        onUploaded={handleFilesUploaded}
      />

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}
        PaperProps={{ 'data-testid': 'chat_deleteconvo_dlg' } as any}>
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this conversation?</Typography>
        </DialogContent>
        <DialogActions>
          <FancyButton testId="chat_deleteconvo_cancel_btn" onClick={() => setDeleteOpen(false)}>Cancel</FancyButton>
          <FancyButton solid color="#DC143C" testId="chat_deleteconvo_confirm_btn"
            onClick={() => { setDeleteOpen(false); handleDeleteConvo() }}>
            Delete
          </FancyButton>
        </DialogActions>
      </Dialog>

      {/* ── Left panel ────────────────────────────────────────────────── */}
      <Box
        ref={leftPanelRef}
        sx={{
          flex: `0 0 ${sidebarWidth}px`,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Conversation history section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: `0 0 ${convoHeight}px`, overflow: 'hidden', minHeight: 0 }}>

          <Box data-testid="chat_convos_toolbar" sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75, gap: 0.25, flexShrink: 0, bgcolor: 'action.hover' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>Conversations</Typography>
            <Tooltip title="New Conversation">
              <IconButton size="small" data-testid="chat_newconvo_btn" onClick={() => setCurrentConvoIndex(-1)}>
                <AddCommentOutlinedIcon sx={{ fontSize: ICON_MD }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Conversation">
              <span>
                <IconButton size="small" data-testid="chat_deleteconvo_btn"
                  disabled={currentConvoIndex < 0} onClick={() => setDeleteOpen(true)}>
                  <DeleteOutlineIcon sx={{ fontSize: ICON_MD }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton size="small" data-testid="chat_refreshconvo_btn" onClick={() => loadConvos()}>
                <RefreshIcon sx={{ fontSize: ICON_MD }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider />

          {hasConvos ? (
            <List disablePadding data-testid="chat_convo_list" sx={{ flex: 1, overflowY: 'auto' }}>
              {convoList.map((c, i) => {
                const prev = convoList[i - 1]
                const showHeader = !prev || c.datecategory !== prev.datecategory
                return (
                  <Box key={c.convosha ?? i}>
                    {showHeader && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', px: 1.5, pt: 0.75, pb: 0.25, fontWeight: 600, opacity: 0.55, fontSize: FS_MICRO, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                      >
                        {DATE_LABELS[c.datecategory] ?? c.datecategory}
                      </Typography>
                    )}
                    <ListItemButton
                      data-testid="chat_convo_row"
                      data-convo-sha={c.convosha ?? ''}
                      data-convo-index={i}
                      selected={i === currentConvoIndex}
                      onClick={() => handleSelectConvo(i)}
                      sx={{ flexDirection: 'column', alignItems: 'flex-start', px: 1.5, py: 0.5 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                        <AccessTimeOutlinedIcon sx={{ fontSize: 11, opacity: 0.5 }} />
                        <Typography variant="caption" sx={{ opacity: 0.5, fontSize: FS_MICRO }}>
                          {typeof c.updatetime === 'string' ? c.updatetime.slice(0, 10) : ''}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: FS_XS,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          width: '100%',
                        }}
                        dangerouslySetInnerHTML={{ __html: c.title ?? c.messagelist?.[0]?.q ?? 'Untitled' }}
                      />
                    </ListItemButton>
                  </Box>
                )
              })}
            </List>
          ) : (
            <Box sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">No conversations yet.</Typography>
            </Box>
          )}
        </Box>

        {/* Vertical resize handle */}
        <Box
          onMouseDown={handleVertResizeStart}
          sx={{
            height: 7,
            flexShrink: 0,
            cursor: 'row-resize !important',
            borderTop: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& *': { cursor: 'row-resize !important' },
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <MoreHorizIcon sx={{ fontSize: ICON_NAV, opacity: 0.35 }} />
        </Box>

        {/* Folders section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {/* <Box sx={{ display: 'flex', alignItems: 'center', px: 1.25, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, bgcolor: 'action.hover' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: FS_SM, flexShrink: 0 }}>
              {hasSelectedFolders
                ? `AI will search ${selectedFolders.size} folder${selectedFolders.size !== 1 ? 's' : ''}`
                : 'Tick folders to scope the AI search'}
            </Typography>
          </Box> */}

          <KbFolderPanel
            currentPath={folderPath}
            currentDirName={folderDir?.name}
            folders={folders}
            files={folderFiles}
            refreshing={foldersRefreshing}
            folderTab={folderTab}
            onFolderTabChange={setFolderTab}
            onOpenFolder={openFolder}
            onGoHome={goHome}
            onGoUp={goUp}
            selectedFolderShas={selectedShaSet}
            onFolderToggle={handleFolderToggle}
            onDropFiles={openAddFilesWithDropped}
          />
        </Box>
      </Box>

      {/* ── Horizontal resize handle ───────────────────────────────────── */}
      <Box
        onMouseDown={handleResizeStart}
        sx={{
          width: 7,
          flexShrink: 0,
          cursor: 'col-resize !important',
          borderLeft: 1,
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& *': { cursor: 'col-resize !important' },
          '&:hover': { bgcolor: 'action.hover' },
          zIndex: 1,
        }}
      >
        <MoreVertIcon sx={{ fontSize: ICON_NAV, opacity: 0.35 }} />
      </Box>

      {/* ── Right panel ───────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minWidth: 0 }}>
        <ChatPanel
          externalMessages={currentConvo?.messagelist ?? []}
          externalIsProcessing={isProcessing}
          externalStreamingText={streamingText}
          externalStreamingTools={streamingTools}
          externalStreamingThinking={streamingThinking}
          externalStreamingIterations={streamingIterations}
          externalStreamingUsage={streamingUsage}
          onExternalAsk={handleAsk}
          onExternalStop={handleStop}
          onResend={handleResend}
          hideCloseButton
          fillWhenEmpty
          toolbarScopeContent={toolbarScopeContent}
          toolbarRightContent={toolbarRightContent}
          emptyState={
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1, opacity: 0.5 }}>
              <SmartToyOutlinedIcon sx={{ fontSize: 40 }} />
              <Typography variant="body2">Start a new conversation</Typography>
              {hasSelectedFolders && (
                <Typography variant="caption" color="text.secondary">
                  Searching {selectedFolders.size} folder{selectedFolders.size !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          }
        />
      </Box>
    </Box>
  )
}
